import { black, brand, slate, white } from '@pelotoncycle/design-system';
import { rgba } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { media } from '@peloton/styles';
import type { TrackingProps } from '@ecomm/analytics';
import type { ImageType } from '@ecomm/copy';
import { BlockPerformantImage } from '@ecomm/themed-modules/ProductBlock/styles';
import { b1, b3, bold, hb6, hb8 } from '@ecomm/typography';

export type Props = {
  header: string;
  page: string;
  body?: string;
  link: string;
  image: ImageType | null;
  trackLinkClick: (props: TrackingProps) => void;
  cardIndex: number;
};

const BlogCard: React.FC<React.PropsWithChildren<Props>> = ({
  header,
  body,
  link,
  image,
  trackLinkClick,
  cardIndex,
  page,
}) => (
  <ZoomableBlogCard
    href={link}
    onClick={event => {
      trackLinkClick({
        parent: 'Blog Module',
        href: link,
        additionalProps: {
          unitName: 'Blog Article Link',
        },
      });
    }}
  >
    <StyledImageContainer>
      {image && (
        <StyledImage
          alt={image.mobile.description}
          breakpointOptions={{
            mobile: {
              src: image.mobile.url,
              width: 380,
            },
            tablet: {
              src: image.tablet.url,
              width: 580,
            },
            tabletXLarge: {
              src: image.desktop.url,
              width: 768,
            },
            desktopLarge: {
              src: image.desktopLarge.url,
              width: 256,
            },
          }}
          loading="lazy"
          src={image.mobile.url}
        />
      )}
    </StyledImageContainer>
    <FloatingCard>
      <Heading data-test-id={`${page}BlogCardTitle-${cardIndex}`}>{header}</Heading>
      <StyledHr />
      {body && <Body>{body}</Body>}
    </FloatingCard>
  </ZoomableBlogCard>
);

const FloatingCard = styled.div`
  background-color: ${white};
  padding: 24px;
  border-radius: 3px;
  box-shadow: 0 8px 18px 2px ${rgba(black, 0.1)};
  margin: -36px 16px 0;
  transform: translateY(0);
  transition: all 500ms ease-in-out;

  ${media.desktop`
    padding: 40px 80px;
  `}

  ${media.desktopLarge`
    padding: 25px;
  `}

  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ZoomableBlogCard = styled.a.attrs({ target: '_blank' })`
  display: flex;
  flex-direction: column;

  &:hover,
  &:focus {
    picture {
      transform: scale(1.03);
    }

    ${FloatingCard} {
      transform: translateY(-2%);
    }
  }

  text-align: center;

  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }

  ${media.desktop`
    margin-bottom: 40px;
  `}

  ${media.desktopLarge`
    margin: 0 12.5px;
    max-width: 384px;
  `}
`;

const StyledHr = styled.hr`
  margin: 16px auto;
  width: 40px;
  height: 1px;
  border: 0.5px solid ${brand.primary};
`;

const StyledImageContainer = styled.div`
  overflow: hidden;
`;

const StyledImage = styled(BlockPerformantImage)`
  img {
    width: 100%;
  }
`;

const Heading = styled.h3`
  ${bold}
  ${hb8}
  color: ${slate[50]};

  ${media.desktop`
    ${hb6}
  `}

  ${media.desktopLarge`
    ${hb8}
  `}
`;

const Body = styled.p`
  ${b3}

  ${media.desktop`
    ${b1}
  `}

  ${media.desktopLarge`
    ${b3}
    margin: 0 auto;
    max-width: 14em;
  `}
`;

export default BlogCard;
